import type { ChangeEvent, ComponentProps } from 'react'
import React, { useState, useRef } from 'react'
import { Label } from '~/design-system/primitives'
import { cn } from '~/lib/cn'

type onDueDateChangeProps = { year: string; month: string; isValid: boolean }
type DueDateInputProps = {
  onDueDateChange?: (props: onDueDateChangeProps) => void
  monthInputProps?: ComponentProps<'input'>
  yearInputProps?: ComponentProps<'input'>
}

const DueDateInput: React.FC<DueDateInputProps> = ({ monthInputProps, yearInputProps, onDueDateChange }) => {
  const [error, setError] = useState<string | null>(null)
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')

  // TODO
  const monthRef = useRef(month)
  const yearRef = useRef(year)

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>, type: 'month' | 'year') => {
    const value = e.target.value.replace(/\D/g, '') // remove non-numeric characters
    setError(null)

    if (type === 'month') {
      monthRef.current = value
      setMonth(value)
    } else if (type === 'year') {
      yearRef.current = value
      setYear(value)
    }

    if (monthRef.current && yearRef.current) {
      const { isValid, errorMessage } = validateDueDate(monthRef.current, yearRef.current)
      setError(errorMessage)
      if (onDueDateChange) {
        onDueDateChange({
          year: yearRef.current,
          month: monthRef.current,
          isValid,
        })
      }
    }

    if (type === 'month') {
      validateMonth(value)
    } else if (type === 'year') {
      validateYear(value)
    }
  }

  const validateDueDate = (monthValue: string, yearValue: string) => {
    const today = new Date()
    const currentMonth = today.getMonth() + 1
    const currentYear = today.getFullYear() % 100

    const expMonth = parseInt(monthValue, 10)
    const expYear = parseInt(yearValue, 10)

    if (!monthValue || !yearValue) {
      return { isValid: false, errorMessage: 'Mês e ano são obrigatórios.' }
    }

    if (!/^\d{2}$/.test(monthValue) || expMonth < 1 || expMonth > 12) {
      return { isValid: false, errorMessage: 'O mês deve estar entre 01 e 12 e ter dois dígitos.' }
    }

    if (!/^\d{2}$/.test(yearValue)) {
      return { isValid: false, errorMessage: 'O ano deve ter dois dígitos.' }
    }

    if (expYear < currentYear) {
      return { isValid: false, errorMessage: 'O cartão está expirado.' }
    }

    if (expYear === currentYear && expMonth < currentMonth) {
      return { isValid: false, errorMessage: 'O cartão está expirado.' }
    }

    return { isValid: true, errorMessage: null }
  }

  const validateMonth = (monthValue: string) => {
    const today = new Date()
    const currentMonth = today.getMonth() + 1
    const currentYear = today.getFullYear() % 100
    const expMonth = parseInt(monthValue, 10)
    const expYear = parseInt(year, 10)

    if (!/^\d{2}$/.test(monthValue) || expMonth < 1 || expMonth > 12) {
      setError('O mês deve estar entre 01 e 12 e ter dois dígitos.')
      return
    }

    if (expYear === currentYear && expMonth < currentMonth) {
      setError('O mês deve ser o atual ou posterior deste ano.')
    }
  }

  const validateYear = (yearValue: string) => {
    const today = new Date()
    const currentYear = today.getFullYear() % 100
    const expYear = parseInt(yearValue, 10)

    if (expYear < currentYear) {
      setError('O ano deve ser o atual ou maior.')
    }
  }

  return (
    <>
      <Label htmlFor="month">Vencimento *</Label>
      <label className="sr-only" htmlFor="year">
        ano
      </label>

      <div
        className={cn(
          'flex max-w-full lg:max-w-[116px] w-full text-sm text-gray-800  max-h-9 bg-white border-gray-60 border rounded px-1',
          error && ' border-red-700',
        )}
      >
        <input
          className="text-center border-0 px-2  py-1.5 w-1/2 outline-0 my-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          id="month"
          maxLength={2}
          name="month"
          placeholder="MM"
          type="tel"
          value={month}
          onChange={(e) => handleDateChange(e, 'month')}
          {...monthInputProps}
        />
        <span className="flex h-full items-center justify-center">/</span>
        <input
          className="text-center border-0 px-2 py-1.5  w-1/2 outline-0 my-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          id="year"
          maxLength={2}
          name="year"
          placeholder="AA"
          type="tel"
          value={year}
          onChange={(e) => handleDateChange(e, 'year')}
          {...yearInputProps}
        />
      </div>

      {error ? (
        <p className="text-red-700 text-sm">{error}</p>
      ) : (
        <p className="text-sm text-gray-800">Mês e ano</p>
      )}
    </>
  )
}

export default DueDateInput
